import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { startPointerDrag } from '../Utils/pointerDrag';
import { defaultPanelPosition, ensurePanelLayoutVersion } from '../ui/panelLayout';

const STORAGE_PREFIX = 'mdv-panel-pos:';

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function readStored(id) {
  try {
    ensurePanelLayoutVersion();
    const raw = localStorage.getItem(STORAGE_PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeStored(id, pos) {
  try {
    localStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(pos));
  } catch {
    /* ignore */
  }
}

function applyNodePos(node, next) {
  if (!node || !next) return;
  node.style.left = `${next.left}px`;
  node.style.top = `${next.top}px`;
}

/**
 * Persistable panel position.
 * @param {string} id — must match a key in panelLayout.defaultPanelPosition
 * @param {(() => { left: number, top: number }) | undefined} getDefault — optional override
 */
export function useDraggablePosition(id, getDefault, { onDragMove, onDragEnd, nodeRef } = {}) {
  const [pos, setPos] = useState(() => {
    ensurePanelLayoutVersion();
    const stored = readStored(id);
    if (stored) return stored;
    try {
      return typeof getDefault === 'function' ? getDefault() : defaultPanelPosition(id);
    } catch {
      return defaultPanelPosition(id);
    }
  });

  const dragRef = useRef(null);
  const stopDragRef = useRef(null);
  const livePosRef = useRef(pos);
  const callbacksRef = useRef({ onDragMove, onDragEnd });
  callbacksRef.current = { onDragMove, onDragEnd };

  if (!dragRef.current) {
    livePosRef.current = pos;
  }

  useEffect(() => {
    const keepInView = () => {
      if (dragRef.current) return;
      setPos((prev) => {
        const maxL = Math.max(8, window.innerWidth - 48);
        const maxT = Math.max(8, window.innerHeight - 48);
        const next = {
          left: clamp(prev.left, 8, maxL),
          top: clamp(prev.top, 8, maxT),
        };
        if (next.left === prev.left && next.top === prev.top) return prev;
        writeStored(id, next);
        return next;
      });
    };
    keepInView();
    window.addEventListener('resize', keepInView);
    return () => window.removeEventListener('resize', keepInView);
  }, [id]);

  useLayoutEffect(() => {
    if (!dragRef.current) return;
    applyNodePos(nodeRef?.current, livePosRef.current);
  });

  useEffect(
    () => () => {
      if (!stopDragRef.current) return;
      stopDragRef.current();
      stopDragRef.current = null;
      dragRef.current = null;
      writeStored(id, livePosRef.current);
    },
    [id]
  );

  const setPosAndStore = useCallback(
    (next) => {
      setPos((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        const maxL = Math.max(8, window.innerWidth - 48);
        const maxT = Math.max(8, window.innerHeight - 48);
        const clamped = {
          left: clamp(resolved.left, 8, maxL),
          top: clamp(resolved.top, 8, maxT),
        };
        writeStored(id, clamped);
        livePosRef.current = clamped;
        return clamped;
      });
    },
    [id]
  );

  const onDragHandlePointerDown = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      const startX = e.clientX;
      const startY = e.clientY;
      const origin = { ...livePosRef.current };
      dragRef.current = { startX, startY, origin };

      stopDragRef.current = startPointerDrag(e, {
        onMove: (ev) => {
          if (!dragRef.current) return;
          const dx = ev.clientX - dragRef.current.startX;
          const dy = ev.clientY - dragRef.current.startY;
          const maxL = Math.max(8, window.innerWidth - 48);
          const maxT = Math.max(8, window.innerHeight - 48);
          const next = {
            left: clamp(dragRef.current.origin.left + dx, 8, maxL),
            top: clamp(dragRef.current.origin.top + dy, 8, maxT),
          };
          livePosRef.current = next;
          applyNodePos(nodeRef?.current, next);
          const moved = Math.abs(dx) > 10 || Math.abs(dy) > 10;
          callbacksRef.current.onDragMove?.(next, ev, {
            moved,
            originLeft: dragRef.current.origin.left,
          });
        },
        onEnd: (ev) => {
          const origin = dragRef.current;
          const current = livePosRef.current;
          const moved = origin
            ? Math.abs(current.left - origin.origin.left) > 10 ||
              Math.abs(current.top - origin.origin.top) > 10
            : false;
          const originLeft = origin?.origin?.left;
          dragRef.current = null;
          stopDragRef.current = null;
          setPosAndStore(current);
          callbacksRef.current.onDragEnd?.(current, ev, { moved, originLeft });
        },
      });
    },
    [nodeRef, setPosAndStore]
  );

  return { pos, setPos: setPosAndStore, onDragHandlePointerDown };
}
